import { useEffect, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CSV_LIMITS, csvToObjects, overLimit, type CsvDelimiter, type CsvEncoding, type CsvImportReport, type SceneStore } from '@os/doc';
import type { Point } from '@os/engine';
import { Button, Dialog, Field, FileButton, RadioGroup, Select, Switch, cn } from '../../../ui';
import { OBJECT_COLORS } from '../../../ui/palette';
import { SECTION_COLORS } from '../../../ui/section-colors';
import { readCsvFile, type CsvRead, type CsvReadOptions } from './read-csv';

const PREVIEW_ROWS = 5;
const STICKY = OBJECT_COLORS.find((c) => c.id === 'yellow')!.hex;
const SECTION = SECTION_COLORS.find((c) => c.id === 'gray')!.fill;
const DELIMITERS: { value: CsvDelimiter; key: string }[] = [
  { value: ',', key: 'comma' },
  { value: ';', key: 'semicolon' },
  { value: '\t', key: 'tab' },
];

type Props = {
  store: SceneStore;
  /** Where the import is centred: the view's centre. A new page is centred on its origin. */
  at: Point;
  onClose: () => void;
  /** The imported top-level objects, and the new page when there is one (the editor selects and shows them). */
  onInserted?: (ids: string[], pageId: string | null) => void;
  read?: (file: File, opts: CsvReadOptions) => Promise<CsvRead>;
};

/**
 * File › Import › CSV (FigJam) (backlog §17.4): choose or drop a file, check its first rows, the delimiter and the
 * header, pick tables or stickies, here or on a new page; one insertion, so one Undo removes it. Read in the browser
 * only, shown as plain text.
 */
export function CsvImportDialog({ store, at, onClose, onInserted, read = readCsvFile }: Props) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [opts, setOpts] = useState<CsvReadOptions>({});
  const [parsed, setParsed] = useState<CsvRead | null>(null);
  const [header, setHeader] = useState(true);
  const [destination, setDestination] = useState<'table' | 'sticky'>('table');
  const [place, setPlace] = useState<'view' | 'page'>('view');
  const [report, setReport] = useState<(CsvImportReport & { truncatedCells: number }) | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (!file) return;
    let live = true;
    void read(file, opts).then((r) => live && setParsed(r));
    return () => {
      live = false;
    };
  }, [file, opts, read]);

  const choose = (f: File | undefined) => {
    if (!f) return;
    setParsed(null);
    setOpts({});
    setFile(f);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    choose(e.dataTransfer.files[0]);
  };
  const name = file ? file.name.replace(/\.[^.]+$/, '') || file.name : '';
  const ok = parsed?.ok ? parsed : null;
  const limit = parsed && !parsed.ok && parsed.reason === 'too_large' ? 'tooLarge' : ok?.error === 'too_many_rows' ? 'tooManyRows' : ok?.error === 'too_many_cols' ? 'tooManyCols' : null;

  const run = () => {
    if (!ok || store.readOnly) return;
    const { items, report: r } = csvToObjects(ok.rows, {
      destination, header, name: file?.name ?? name, stickyColor: STICKY, sectionFill: SECTION, at: place === 'page' ? { x: 0, y: 0 } : at,
    });
    let ids: string[] = [];
    let pageId: string | null = null;
    if (overLimit(store.counts(), { objects: items.length, images: 0 })) return setFailed(t('csvImport.errors.boardFull'));
    if (place === 'page') {
      const out = store.commands.insertPages([{ name: name.slice(0, 100) || t('csvImport.pageName'), items }]);
      if (!out) return setFailed(t('csvImport.errors.tooManyPages'));
      ids = out.ids;
      pageId = out.pageIds[0] ?? null;
    } else ids = store.commands.insertObjects(items);
    const top = ids.filter((id) => store.getObject(id)?.parentId === null);
    setReport({ ...r, truncatedCells: ok.truncatedCells });
    onInserted?.(top, pageId);
  };

  const footer = report ? (
    <Button variant="primary" onClick={onClose}>
      {t('csvImport.close')}
    </Button>
  ) : (
    <>
      <Button onClick={onClose}>{t('csvImport.close')}</Button>
      {ok && !limit && (
        <Button variant="primary" onClick={run} disabled={store.readOnly || ok.rows.length === 0}>
          {t('csvImport.import')}
        </Button>
      )}
    </>
  );

  return (
    <Dialog title={t('csvImport.title')} description={t('csvImport.description')} onClose={onClose} footer={footer} size="lg">
      {report ? (
        <div role="status" className="flex flex-col gap-1">
          <p>{t('csvImport.report.rows', { count: report.rows })}</p>
          {report.tables > 1 && <p>{t('csvImport.report.tables', { count: report.tables })}</p>}
          {report.truncatedCells > 0 && <p>{t('csvImport.report.truncated', { count: report.truncatedCells, max: CSV_LIMITS.cell })}</p>}
          {report.skippedEmpty > 0 && <p className="text-fg-muted">{t('csvImport.report.empty', { count: report.skippedEmpty })}</p>}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div
            data-testid="csv-drop"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border-strong p-5 text-center text-fg-muted"
          >
            <p>{file ? file.name : t('csvImport.drop')}</p>
            <FileButton accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain" onFiles={([f]) => choose(f)}>
              {file ? t('csvImport.another') : t('csvImport.choose')}
            </FileButton>
          </div>
          {parsed && !parsed.ok && parsed.reason === 'not_utf8' && (
            <div role="alert" className="flex flex-col items-start gap-2">
              <p>{t('csvImport.notUtf8')}</p>
              <Button onClick={() => setOpts((o) => ({ ...o, encoding: 'windows-1252' as CsvEncoding }))}>{t('csvImport.readCp1252')}</Button>
            </div>
          )}
          {failed && (
            <p role="alert" className="text-danger">
              {failed}
            </p>
          )}
          {limit && (
            <p role="alert" className="text-danger">
              {t(`csvImport.limits.${limit}`, { rows: CSV_LIMITS.rows, cols: CSV_LIMITS.cols })}
            </p>
          )}
          {ok && !limit && (
            <>
              <p className="text-fg-muted">
                {t('csvImport.size', { count: ok.rows.length })}
                {ok.encoding === 'windows-1252' ? ` · ${t('csvImport.readAsCp1252')}` : ''}
              </p>
              <Preview rows={ok.rows} header={header} label={t('csvImport.preview')} />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('csvImport.delimiter')}>
                  <Select value={ok.delimiter} onChange={(e) => setOpts((o) => ({ ...o, delimiter: e.target.value as CsvDelimiter }))}>
                    {DELIMITERS.map((d) => (
                      <option key={d.key} value={d.value}>
                        {t(`csvImport.delimiters.${d.key}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Switch label={t('csvImport.header')} checked={header} onCheckedChange={setHeader} className="self-end pb-2" />
                <RadioGroup
                  label={t('csvImport.as')}
                  value={destination}
                  onValueChange={setDestination}
                  options={[
                    { value: 'table', label: t('csvImport.asTable') },
                    { value: 'sticky', label: t('csvImport.asSticky') },
                  ]}
                />
                <RadioGroup
                  label={t('csvImport.place')}
                  value={place}
                  onValueChange={setPlace}
                  options={[
                    { value: 'view', label: t('csvImport.placeView') },
                    { value: 'page', label: t('csvImport.placePage') },
                  ]}
                />
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** The first rows, as plain text; the table scrolls sideways inside the dialog, never the page. */
function Preview({ rows, header, label }: { rows: string[][]; header: boolean; label: string }) {
  const head = header ? rows[0] : undefined;
  const body = rows.slice(header ? 1 : 0, PREVIEW_ROWS + (header ? 1 : 0));
  const cell = 'max-w-48 truncate border-b border-border px-2 py-1 text-left';
  return (
    <div className="max-w-full overflow-x-auto overscroll-x-contain rounded-xl border border-border">
      <table aria-label={label} className="min-w-full border-collapse text-xs">
        {head && (
          <thead>
            <tr>
              {head.map((c, i) => (
                <th key={i} scope="col" className={cn(cell, 'font-semibold')}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {body.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} className={cell}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
